import i18n from 'i18next';
import { startCase } from 'lodash';

import {
  type AtlasGqlTaskTargetInput,
  AtlasGqlTaskTargetType,
  GetDamageDocument,
  GetHorizonDamageTasksDocument,
} from 'types/atlas-graphql';

import type { TMessageContent } from './types';

export const messageContent = (input: TMessageContent, route: string = 'tasks') => {
  const { type, number, id } = input;
  return (
    <a id={'task-create-success-message'} href={`/${route}/${id}`}>
      {i18n.t('tasks.taskCreatedClickToView', '{{typeLabel}} {{number}} created. Click to view.', {
        typeLabel: startCase(type),
        number,
      })}
    </a>
  );
};

/**
 * After task creation from a damage, the damage detail query needs to be refetched
 * as the cache will not automatically be updated. When created, damage-related tasks
 * are associated with either a horizonDamageId or a (legacy) damageBranchId, but not both.
 *
 * Given props for the <CreateTask /> component, this helper returns the queries that should
 * be refetched after task creation, to be used for the `refetchQueries` option of `useMutation`.
 */
export const getRefetchQueriesForTaskCreation = ({
  horizonDamageId,
  damageBranchId,
}: {
  horizonDamageId?: string;
  damageBranchId?: string;
}) => {
  if (horizonDamageId) {
    return [
      {
        query: GetHorizonDamageTasksDocument,
        variables: { input: { id: horizonDamageId } },
      },
    ];
  }

  if (damageBranchId) {
    return [
      {
        query: GetDamageDocument,
        variables: { id: damageBranchId },
      },
    ];
  }

  return [];
};

export const getTaskTargetInput = ({
  inspectionId,
  horizonDamageId,
}: {
  inspectionId?: string;
  horizonDamageId?: string;
}): AtlasGqlTaskTargetInput[] =>
  [
    inspectionId && {
      targetId: inspectionId,
      targetType: AtlasGqlTaskTargetType.Inspection,
    },
    horizonDamageId && {
      targetId: horizonDamageId,
      targetType: AtlasGqlTaskTargetType.HorizonDamage,
    },
  ].filter((t): t is AtlasGqlTaskTargetInput => !!t);
